<template>
  <div class="main-menu">
    <div class="mobile-logo-area d-lg-none d-flex align-items-center justify-content-between">
      <router-link :to="buildRoute('/')" class="mobile-logo-wrap">
        <img :src="flavor.logos.menu" :alt="flavor.displayName">
      </router-link>
      <div class="menu-close-btn">
        <i class="bi bi-x"></i>
      </div>
    </div>
    
    <ul class="menu-list">
      <li class="menu-item-has-children" :class="{ active: isActive('/') }">
        <router-link :to="buildRoute('/')" class="drop-down">
          Home
        </router-link>
      </li>

      <li class="menu-item-has-children position-inherit">
        <a href="#" class="drop-down">
          Locations
          <i class="bi bi-caret-down-fill"></i>
        </a>
        <i class="bi bi-plus dropdown-icon"></i>
        
        <div class="mega-menu">
          <div class="container">
            <div class="locations-grid">
              <router-link
                v-for="property in locationProperties"
                :key="property.slug"
                :to="buildRoute({
                  name: 'property-details',
                  params: {
                    slug: property.slug,
                    propertyId: property.id || undefined
                  }
                })"
                class="location-card"
              >
                <div class="location-card-thumb">
                  <img
                    v-if="property.image"
                    :src="property.image"
                    :alt="property.name"
                    loading="lazy"
                  />
                  <div v-else class="location-card-placeholder">
                    <i class="bi bi-building"></i>
                  </div>
                </div>
                <div class="location-card-body">
                  <span class="location-card-name">{{ property.name }}</span>
                  <span v-if="property.location" class="location-card-sub">
                    <i class="bi bi-geo-alt"></i> {{ property.location }}
                  </span>
                </div>
              </router-link>
            </div>
            <div class="locations-footer">
              <router-link :to="buildRoute('/properties')" class="locations-view-all">
                View All Properties <i class="bi bi-arrow-right"></i>
              </router-link>
            </div>
          </div>
        </div>
      </li>

      <li class="menu-item-has-children" :class="{ active: isActive('/properties') }">
        <router-link :to="buildRoute('/properties')" class="drop-down">
          Properties
        </router-link>
      </li>

      <li class="menu-item-has-children" :class="{ active: isActive('/my-bookings') }">
        <a href="#" class="drop-down" @click.prevent="handleAuthLink('my-bookings')">
          My Bookings
        </a>
      </li>

      <li class="menu-item-has-children" :class="{ active: isActive('/my-inquiries') }">
        <a href="#" class="drop-down" @click.prevent="handleAuthLink('my-inquiries')">
          My Inquiries
        </a>
      </li>
    </ul>

    <!-- Mobile Contact Area -->
    <div ref="mobileContactRef" class="contact-area d-lg-none d-flex">
      <div class="single-contact">
        <div class="icon">
          <img src="/assets/img/home1/icon/whatsapp-icon.svg" alt="">
        </div>
        <div class="content">
          <span>{{ whatsappLabel }}</span>
          <a :href="whatsappHref">{{ whatsappDisplay }}</a>
        </div>
      </div>
      <i
        class="bi bi-caret-down-fill contact-dropdown-btn"
        role="button"
        tabindex="0"
        @click.stop="toggleMobileContactDropdown"
      ></i>
      <ul class="contact-list" :class="{ active: isMobileContactOpen }">
        <li class="single-contact">
          <div class="icon">
            <img src="/assets/img/home1/icon/mail-icon.svg" alt="">
          </div>
          <div class="content">
            <span>{{ emailLabel }}</span>
            <a :href="emailHref">{{ emailValue }}</a>
          </div>
        </li>
        <li class="single-contact">
          <div class="icon">
            <img src="/assets/img/home1/icon/whatsapp-icon.svg" alt="">
          </div>
          <div class="content">
            <span>{{ phoneLabel }}</span>
            <a :href="phoneHref">{{ phoneDisplay }}</a>
          </div>
        </li>
      </ul>
    </div>

    <!-- Mobile Login Button -->
    <button type="button" class="primary-btn1 black-bg d-lg-none d-flex menu-login-btn" @click="openLoginModal">
      <span>
        <svg width="15" height="15" viewBox="0 0 15 15" xmlns="http://www.w3.org/2000/svg">
          <g>
            <path d="M7.50105 7.78913C9.64392 7.78913 11.3956 6.03744 11.3956 3.89456C11.3956 1.75169 9.64392 0 7.50105 0C5.35818 0 3.60652 1.75169 3.60652 3.89456C3.60652 6.03744 5.35821 7.78913 7.50105 7.78913ZM14.1847 10.9014C14.0827 10.6463 13.9467 10.4082 13.7936 10.1871C13.0113 9.0306 11.8038 8.2653 10.4433 8.07822C10.2732 8.06123 10.0861 8.09522 9.95007 8.19727C9.23578 8.72448 8.38546 8.99658 7.50108 8.99658C6.61671 8.99658 5.76638 8.72448 5.05209 8.19727C4.91603 8.09522 4.72895 8.04421 4.5589 8.07822C3.19835 8.2653 1.97387 9.0306 1.20857 10.1871C1.05551 10.4082 0.919443 10.6633 0.817424 10.9014C0.766415 11.0034 0.783407 11.1225 0.834416 11.2245C0.970484 11.4626 1.14054 11.7007 1.2936 11.9048C1.53168 12.2279 1.78679 12.517 2.07592 12.7891C2.31401 13.0272 2.58611 13.2483 2.85824 13.4694C4.20177 14.4728 5.81742 15 7.48409 15C9.15076 15 10.7664 14.4728 12.1099 13.4694C12.382 13.2653 12.6541 13.0272 12.8923 12.7891C13.1644 12.517 13.4365 12.2279 13.6746 11.9048C13.8446 11.6837 13.9977 11.4626 14.1338 11.2245C14.2188 11.1225 14.2358 11.0034 14.1847 10.9014Z"/>
          </g>
        </svg>
        Login
      </span>
      <span>
        <svg width="15" height="15" viewBox="0 0 15 15" xmlns="http://www.w3.org/2000/svg">
          <g>
            <path d="M7.50105 7.78913C9.64392 7.78913 11.3956 6.03744 11.3956 3.89456C11.3956 1.75169 9.64392 0 7.50105 0C5.35818 0 3.60652 1.75169 3.60652 3.89456C3.60652 6.03744 5.35821 7.78913 7.50105 7.78913ZM14.1847 10.9014C14.0827 10.6463 13.9467 10.4082 13.7936 10.1871C13.0113 9.0306 11.8038 8.2653 10.4433 8.07822C10.2732 8.06123 10.0861 8.09522 9.95007 8.19727C9.23578 8.72448 8.38546 8.99658 7.50108 8.99658C6.61671 8.99658 5.76638 8.72448 5.05209 8.19727C4.91603 8.09522 4.72895 8.04421 4.5589 8.07822C3.19835 8.2653 1.97387 9.0306 1.20857 10.1871C1.05551 10.4082 0.919443 10.6633 0.817424 10.9014C0.766415 11.0034 0.783407 11.1225 0.834416 11.2245C0.970484 11.4626 1.14054 11.7007 1.2936 11.9048C1.53168 12.2279 1.78679 12.517 2.07592 12.7891C2.31401 13.0272 2.58611 13.2483 2.85824 13.4694C4.20177 14.4728 5.81742 15 7.48409 15C9.15076 15 10.7664 14.4728 12.1099 13.4694C12.382 13.2653 12.6541 13.0272 12.8923 12.7891C13.1644 12.517 13.4365 12.2279 13.6746 11.9048C13.8446 11.6837 13.9977 11.4626 14.1338 11.2245C14.2188 11.1225 14.2358 11.0034 14.1847 10.9014Z"/>
          </g>
        </svg>
        Login
      </span>
    </button>
  </div>
</template>

<script setup>
import { computed, inject, onBeforeUnmount, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { usePropertiesStore } from '@/stores/properties'
import { resolveFlavor } from '@/config/flavors'
import { useAuthModalStore } from '@/stores/authModal'
import { useAuthStore } from '@/stores/auth'
import { useAccountRoute } from '@/composables/useAccountRoute'

const route = useRoute()
const router = useRouter()
const { buildRoute } = useAccountRoute()
const propertiesStore = usePropertiesStore()
const authStore = useAuthStore()

const locationProperties = computed(() => {
  const properties = propertiesStore.catalogProperties || []
  return properties
    .filter(p => p?.slug)
    .map(p => {
      const city = (p.location?.city || '').trim()
      const area = (p.location?.area || '').trim()
      const address = (p.location?.address || '').trim()
      // Build a readable location line — skip numeric-only city codes
      const isNumericCity = /^\d+$/.test(city)
      const displayCity = isNumericCity ? '' : city
      const locationParts = [area, displayCity].filter(Boolean)
      const location = locationParts.length ? locationParts.join(', ') : (address ? address.split(',').slice(0, 2).join(',').trim() : '')
      return {
        id: p.id,
        slug: p.slug,
        name: (p.name || '').trim() || 'Unnamed Property',
        image: p.thumbnailUrl || '',
        location
      }
    })
    .sort((a, b) => a.name.localeCompare(b.name))
})
const flavor = inject('flavor', resolveFlavor())
const authModalStore = useAuthModalStore()
const isMobileContactOpen = ref(false)
const mobileContactRef = ref(null)

const toPhoneDigits = (value = '') => String(value).replace(/\D+/g, '')

const quickContact = computed(() => flavor?.contact || {})
const whatsappLabel = computed(() => quickContact.value?.whatsapp?.label || 'WhatsApp')
const whatsappDisplay = computed(() => quickContact.value?.whatsapp?.display || '+91 345 533 865')
const whatsappHref = computed(() => {
  const digits = toPhoneDigits(quickContact.value?.whatsapp?.value)
  return digits ? `https://wa.me/${digits}` : '#'
})

const emailLabel = computed(() => quickContact.value?.email?.label || 'Mail Support')
const emailValue = computed(() => quickContact.value?.email?.value || 'info@sanwariapg.com')
const emailHref = computed(() => {
  const email = String(emailValue.value || '').trim()
  return email ? `mailto:${email}` : '#'
})

const phoneLabel = computed(() => quickContact.value?.phone?.label || 'Phone Support')
const phoneDisplay = computed(() => quickContact.value?.phone?.display || whatsappDisplay.value)
const phoneHref = computed(() => {
  const digits = toPhoneDigits(quickContact.value?.phone?.value || quickContact.value?.whatsapp?.value)
  return digits ? `tel:+${digits}` : '#'
})

const closeMobileContactDropdown = () => {
  isMobileContactOpen.value = false
}

const toggleMobileContactDropdown = () => {
  isMobileContactOpen.value = !isMobileContactOpen.value
}

const handleDocumentClick = (event) => {
  const root = mobileContactRef.value
  if (!root || root.contains(event.target)) {
    return
  }
  closeMobileContactDropdown()
}

onMounted(() => {
  document.addEventListener('click', handleDocumentClick)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', handleDocumentClick)
})

const openLoginModal = () => {
  authModalStore.open('signin')
}

const isActive = (path) => {
  return route.path === path
}

const handleAuthLink = (routeName) => {
  if (authStore.isAuthenticated) {
    router.push(buildRoute({ name: routeName }))
  } else {
    authModalStore.open('signin')
  }
}
</script>

<style scoped>
.menu-login-btn {
  border: none;
}

.mobile-logo-wrap img {
  display: block;
  width: auto;
  max-width: 150px;
  max-height: 52px;
  object-fit: contain;
}

/* ── Locations mega-menu ── */
.locations-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 16px;
  padding: 24px 0 12px;
}

.location-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 12px;
  text-decoration: none;
  color: inherit;
  transition: background 0.2s ease, box-shadow 0.2s ease;
}

.location-card:hover {
  background: #f5f5f7;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
}

.location-card-thumb {
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  border-radius: 10px;
  overflow: hidden;
  background: #f0f0f3;
}

.location-card-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.location-card-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #999;
  font-size: 18px;
}

.location-card-body {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.location-card-name {
  font-size: 14px;
  font-weight: 600;
  color: #1d1d1f;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.3;
}

.location-card-sub {
  font-size: 12px;
  color: #86868b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.3;
  margin-top: 2px;
}

.location-card-sub i {
  font-size: 10px;
  margin-right: 2px;
}

.locations-footer {
  text-align: center;
  padding: 12px 0 20px;
  border-top: 1px solid #f0f0f3;
  margin-top: 4px;
}

.locations-view-all {
  font-size: 13px;
  font-weight: 600;
  color: var(--primary-color1, #1d4ed8);
  text-decoration: none;
  transition: opacity 0.2s;
}

.locations-view-all:hover {
  opacity: 0.75;
}

.locations-view-all i {
  font-size: 11px;
  margin-left: 4px;
  transition: transform 0.2s;
}

.locations-view-all:hover i {
  transform: translateX(3px);
}
</style>
